import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import NavBar from '../../components/layout/NavBar'
import { listAssignedJobs, listMyJobs, type JobsHubItem, type JobsHubTabStatus } from '../../api/jobsHub'
import { getToken } from '../../lib/auth'

type TabKey = 'created' | 'assigned'

const FILTERS: Array<{ key: JobsHubTabStatus; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'active', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'disputed', label: 'Disputed' },
  { key: 'cancelled', label: 'Cancelled' },
]

function statusLabel(job: JobsHubItem): string {
  if (String(job.invitation_status || '') === 'PENDING') return 'Invite pending'
  if (String(job.status || '') === 'DISPUTED') return 'Disputed'
  if (String(job.escrow_status || '') === 'HELD') return 'Escrow funded'
  if (String(job.status || '') === 'COMPLETED' || String(job.status || '') === 'CLOSED') return 'Completed'
  if (String(job.status || '') === 'CANCELLED' || String(job.invitation_status || '') === 'DECLINED') return 'Cancelled'
  if (String(job.status || '') === 'IN_PROGRESS') return 'In progress'
  return 'Open'
}

function pixeAtomicToDisplay(amount?: number | null) {
  if (!amount) return null
  const scale = 1_000_000_000_000n
  const atomic = BigInt(amount)
  const whole = atomic / scale
  const frac = atomic % scale
  if (frac === 0n) return whole.toString()
  const fracStr = frac.toString().padStart(12, '0').replace(/0+$/, '')
  return `${whole}.${fracStr}`
}

export default function JobsHub() {
  const authed = Boolean(getToken())
  const [tab, setTab] = useState<TabKey>('created')
  const [statusFilter, setStatusFilter] = useState<JobsHubTabStatus>('pending')
  const [q, setQ] = useState('')
  const [items, setItems] = useState<JobsHubItem[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!authed) return
    const timer = window.setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      try {
        setLoading(true)
        setError(null)
        const params = { tab_status: statusFilter, q: q.trim() || undefined, page: 1, page_size: 50, signal: controller.signal }
        const data = tab === 'created' ? await listMyJobs(params) : await listAssignedJobs(params)
        setItems(Array.isArray(data?.results) ? data.results : [])
        setCount(Number(data?.count || 0))
      } catch (e: any) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return
        setError(e?.response?.data?.detail || e?.message || 'Failed to load jobs')
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => window.clearTimeout(timer)
  }, [authed, tab, statusFilter, q])

  useEffect(() => () => abortRef.current?.abort(), [])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter((j) => `${j.title || ''} ${j.summary || ''}`.toLowerCase().includes(needle))
  }, [items, q])

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-5xl px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-extrabold">Jobs</h1>
          <div className="text-sm text-white/60">{count} total</div>
        </div>

        {!authed ? (
          <section className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
            Sign in to view your jobs.
          </section>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setTab('created')}
                className={`rounded-full border px-3 py-1.5 text-sm ${tab === 'created' ? 'border-brand bg-brand/20 text-white' : 'border-white/15 bg-white/5 text-white/80'}`}
              >
                Created
              </button>
              <button
                type="button"
                onClick={() => setTab('assigned')}
                className={`rounded-full border px-3 py-1.5 text-sm ${tab === 'assigned' ? 'border-brand bg-brand/20 text-white' : 'border-white/15 bg-white/5 text-white/80'}`}
              >
                Assigned
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setStatusFilter(f.key)}
                  className={`rounded-full border px-3 py-1 text-xs ${statusFilter === f.key ? 'border-brand bg-brand/20 text-white' : 'border-white/15 bg-white/5 text-white/75'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search jobs by title or summary"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm"
            />

            {error ? <div className="rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div> : null}
            {loading ? <div className="glass h-24 rounded-xl" /> : null}
            {!loading && !filtered.length ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
                {statusFilter === 'pending' ? `No pending ${tab === 'created' ? 'jobs' : 'assignments'}.` : 'No jobs found.'}
              </div>
            ) : null}

            <div className="space-y-3">
              {filtered.map((job) => {
                const counterparty = tab === 'created' ? job.pro : job.client
                const updated = job.updated_at ? new Date(job.updated_at).toLocaleString() : ''
                const escrow = pixeAtomicToDisplay(job.escrow_amount_pixe)
                return (
                  <div key={job.id} className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-lg font-semibold truncate">{job.title}</div>
                        {job.summary ? <div className="text-sm text-white/75 line-clamp-2">{job.summary}</div> : null}
                      </div>
                      <span className="rounded-full border border-white/15 bg-black/30 px-2 py-1 text-xs text-white/85">{statusLabel(job)}</span>
                    </div>
                    <div className="text-xs text-white/65">
                      {counterparty?.handle ? `With @${counterparty.handle}` : 'Counterparty pending'}
                      {updated ? ` • Updated ${updated}` : ''}
                      {escrow ? ` • Escrow ${escrow} PIXE` : ''}
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/jobs/${job.id}`} className="rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10">
                        Open
                      </Link>
                      {job.thread_id ? (
                        <Link to={`/inbox/i/${job.thread_id}`} className="rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10">
                          Chat
                        </Link>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </main>
    </div>
  )
}

